import { CvtUploadFailStatus } from '@packages/clients';
import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../sts-backend-operation';
import { handlePresignedUrlsUpload } from '../helpers';

const logger = getLogger('cvt');

/**
 * The parameters required to run the CVT diagnostic process, including the VIN, repair order number, and CVT file to upload.
 */
export type RunDiagnosticParams = {
    /** The Vehicle Identification Number (VIN) of the vehicle associated with the CVT. */
    vin: string;

    /** The repair order number associated with the CVT. */
    repairOrderNumber: string;

    /** The file to be uploaded. */
    file: File;
};

/** The result of running the CVT diagnostic process. */
export type RunDiagnosticResult = {
    /** The STS diagnostic ID associated with the CVT. */
    stsDiagnosticId: string;
};

/**
 * Run the CVT diagnostic process by creating pre-signed upload URLs,
 * uploading the file to the presigned URLs, and transmitting the diagnostic data to SBR to begin processing.
 * Handles upload failures in the event both SBR and STS presigned URL uploads fail,
 * by attempting a fallback upload method where the file is sent directly to the STS backend to upload to S3.
 */
export class RunDiagnostic extends StsBackendOperation {
    /**
     *  Runs the CVT diagnostic process by creating pre-signed upload URLs, uploading the file to the presigned URLs, and transmitting the diagnostic data to SBR to begin processing.
     *
     * @param params - The parameters required to run the CVT diagnostic process, including the VIN, repair order number, and CVT file to upload.
     * @returns The result of running the CVT diagnostic process, including the STS diagnostic ID.
     */
    async exec(params: RunDiagnosticParams): Promise<RunDiagnosticResult> {
        const { file, vin, repairOrderNumber } = params;
        const createUploadUrlsResponse = await this.client.createCvtUploadUrls({ vin, repairOrderNumber });

        if (!createUploadUrlsResponse.success) {
            throw new Error(createUploadUrlsResponse.data?.message);
        }

        const { sbrDiagnosticId, sbrUploadUrl, stsDiagnosticId, stsUploadUrl } = createUploadUrlsResponse.data;

        // Attempt to upload the file to both SBR and STS presigned URLs, and capture any upload failures.
        const { sbrUploadFailureStatus, stsUploadFailureStatus } = await handlePresignedUrlsUpload(
            file,
            sbrUploadUrl,
            stsUploadUrl
        );

        const didStsUploadFail =
            'stsFailStatus' in stsUploadFailureStatus &&
            stsUploadFailureStatus.stsFailStatus === CvtUploadFailStatus.STS;

        // If the STS presigned URL upload failed, attempt to upload the file directly to the STS backend, which will handle uploading the file to S3.
        if (didStsUploadFail) {
            const formUploadFile = new FormData();
            formUploadFile.append('file', file);

            const uploadCvtFileResponse = await this.client.uploadCvtFile({
                diagnosticId: stsDiagnosticId,
                file: formUploadFile,
            });
            if (!uploadCvtFileResponse.success) {
                throw new Error('We are unable to process your request. Please try again.');
            }
        }

        // Notify the backend to begin processing the diagnostic with SBR.
        try {
            const transmitToSbrResponse = await this.client.transmitToSbr({
                stsDiagnosticId,
                sbrDiagnosticId,
                uploadFailureStatus: sbrUploadFailureStatus,
            });

            if (!transmitToSbrResponse.success) {
                throw new Error(transmitToSbrResponse.data?.message || 'Failed to run diagnostic.');
            }

            return { stsDiagnosticId };
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to transmit to SBR';
            logger.error('Failed to run the CVT diagnostic', { error: e });
            throw new Error(message);
        }
    }
}
